import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { UserInfo } from '../models/user-info';
import { Connexion } from '../models/connexion';
import { Router } from '@angular/router';
import { GroupRole } from '../models/group-role';
import { Group } from '../models/group';
import { KeycloakService } from 'keycloak-angular';
import Keycloak from 'keycloak-js';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = environment.apiUrl;
  private userInfoSubject: BehaviorSubject<UserInfo | null> = new BehaviorSubject<UserInfo | null>(null);
  private userInfoLoadedSubject = new BehaviorSubject<boolean>(false);

  private _refreshTokenInProgress = false;
  private _tokenRefreshSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(private http: HttpClient, private router: Router, @Inject(KeycloakService) private keycloakService: KeycloakService) {
    this.userInfoSubject = new BehaviorSubject<UserInfo | null>(null);
  }

  getInfoUser(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/Account/Connecte`);
  }

  getUserInfo(): Observable<UserInfo | null> {
    return this.userInfoSubject.asObservable();
  }

  getUserGroups(): GroupRole[] {
    const userInfo = this.userInfoSubject.value;
    return userInfo ? userInfo.groupeUtilisateur : [];
  }

  async getToken(): Promise<string | null> {
    const token = await this.keycloakService.getToken();
    return token;
  }

  isUserLoggedIn(): boolean {
    return this.keycloakService.isLoggedIn(); // Vérifie si l'utilisateur est déjà connecté dans Keycloak
  }

  logout(): void {
    this.keycloakService.logout(window.location.origin).then(() => {
      localStorage.removeItem('token');
      this.userInfoSubject.next(null);
      this.router.navigate(['/desktop']);
    });
  }

  async login(): Promise<void> {
    try {
      await this.keycloakService.login({ redirectUri: window.location.origin });
      this.loadUserInfo();
    } catch (error) {
      console.error('Erreur lors de la connexion à Keycloak:', error);
    }
  }

  async changePassword(): Promise<void> {
    try {
      const keycloakInstance = this.keycloakService.getKeycloakInstance();
      const realm = keycloakInstance.realm;
      const authServerUrl = keycloakInstance.authServerUrl;
  
      // Construct the URL to directly access the password change page
      const accountUrl = `${authServerUrl}/realms/${realm}/account/#/account-security/signing-in`;
  
      // Redirect the user to the constructed URL
      window.location.href = accountUrl;
    } catch (error) {
      console.error('Erreur lors de la redirection vers la page de gestion du compte:', error);
    }
  }
  
  retrieveAndStoreToken(): void {
    this.keycloakService.getToken()
      .then(token => {
        if (token) {
          localStorage.setItem('token', token); 
        } else {
          console.warn('No token retrieved from Keycloak');
        }
      })
      .catch(err => {
        console.error('Failed to get token from Keycloak:', err);
      });
  }

  loadUserInfo(): Observable<UserInfo | null> {
    if (this.isUserLoggedIn()) {
      return this.getInfoUser().pipe(
        tap(userInfo => {
          this.userInfoSubject.next(userInfo);
          this.userInfoLoadedSubject.next(true);
        }),
        catchError(error => {
          this.userInfoLoadedSubject.next(true);
          return of(null);
        })
      );
    } else {
      this.userInfoLoadedSubject.next(true); 
      return of(null);
    }
  }

  get userInfoLoaded$(): Observable<boolean> {
    return this.userInfoLoadedSubject.asObservable();
  }

  isAdmin(): boolean {
    const userInfo = this.userInfoSubject.value;
    if (!userInfo) {
      return false;
    }

    return userInfo.globalAdmin || userInfo.groupeUtilisateur.some(role =>
      role.manageGroupUsers || role.manageGroupModels || role.manageGroupSensors || role.manageOwnGroup
    );
  }

  isGlobalAdmin(): boolean {
    const userInfo = this.userInfoSubject.value;
    if(userInfo){
      return userInfo?.globalAdmin;
    } 
    return false;
  }

  isBrgm(): boolean {
    const userInfo = this.userInfoSubject.value;
    if (!userInfo) {
      return false;
    }
    // Vérifie si l'utilisateur appartient au groupe BRGM
    return userInfo.groupeUtilisateur.some(role => role.groupe.nom === 'BRGM');
  }

  isPrivileged(): boolean {
    const userInfo = this.userInfoSubject.value;
    if (!userInfo) {
      return false;
    }
    // Vérifie si l'utilisateur a un rôle privilégié
    return userInfo.privileged;
  }

  get refreshTokenInProgress(): boolean {
    return this._refreshTokenInProgress;
  }

  set refreshTokenInProgress(value: boolean) {
    this._refreshTokenInProgress = value;
  }

  get tokenRefreshSubject(): BehaviorSubject<any> {
    return this._tokenRefreshSubject;
  }

  getGroups(): Observable<Group[]> {
    return this.http.get<Group[]>(`${this.apiUrl}/Authorize/Groupes`);
  }
}
